import { computed, toValue, type MaybeRefOrGetter } from 'vue';

import type { AdCampaign, GetAdCampaignsArgs } from '@/composables/platform/types';
import type { Brand } from '@/types/auth.types';
import type { HttpRequestConfig } from '@/types/http';

import { BRAND, USER } from '@/models/auth/permissions.enum';
import { useIdentityStore } from '@/stores/identity';
import { useTiktokStore } from '@/stores/tiktok';

interface TikTokAdAccount {
  sourceAdvertiserId: string;
  name: string;
  currency: string;
  brandIds: number[];
  [key: string]: unknown;
}

/**
 * The slice of the (untyped JS) tiktok store this composable consumes. Declared locally so the
 * ad-account wrapper is type-checked; keep in sync with `@/stores/tiktok`.
 */
interface TiktokAdsStore {
  adAccountsList: TikTokAdAccount[];
  adCampaigns: Record<string, AdCampaign> | null;
  tiktokAdsAccountToCurrency: Record<string, string>;
  pending: { adAccounts: boolean; adCampaigns: boolean };
  error: { adAccounts: unknown; adCampaigns: unknown };
  getAdAccounts(args: { brandIds?: number[] }): Promise<unknown>;
  getAdCampaigns(
    args: { sourceAdAccountIds?: unknown[]; brandIds?: number[]; sourceCampaignIds?: unknown[] },
    axiosConfig?: HttpRequestConfig,
  ): Promise<unknown>;
}

export function useTikTokAdsAccountDetails() {
  const tiktokStore = useTiktokStore() as unknown as TiktokAdsStore;
  const identityStore = useIdentityStore();

  const adAccountsList = computed(() =>
    tiktokStore.adAccountsList.map((account) => ({
      ...account,
      sourceAdAccountId: account.sourceAdvertiserId,
    })),
  );

  const adCampaigns = computed(() => tiktokStore.adCampaigns);

  const adCampaignsList = computed(() => {
    if (!tiktokStore.adCampaigns) return [];
    return Object.values(tiktokStore.adCampaigns);
  });

  const adAccountToCurrency = computed(() => tiktokStore.tiktokAdsAccountToCurrency);

  const adAccountsLoading = computed(() => tiktokStore.pending.adAccounts);
  const adCampaignsLoading = computed(() => tiktokStore.pending.adCampaigns);
  const adAccountsError = computed(() => tiktokStore.error.adAccounts);
  const adCampaignsError = computed(() => tiktokStore.error.adCampaigns);

  async function getAdAccounts({ brandIds }: { brandIds?: MaybeRefOrGetter<number[]> }) {
    await tiktokStore.getAdAccounts({ brandIds: toValue(brandIds) });
  }

  async function getAdCampaigns(
    { brandIds, adAccountIds, sourceCampaignIds }: GetAdCampaignsArgs,
    axiosConfig?: HttpRequestConfig,
  ) {
    await tiktokStore.getAdCampaigns(
      {
        sourceAdAccountIds: toValue(adAccountIds),
        brandIds: toValue(brandIds),
        sourceCampaignIds: toValue(sourceCampaignIds),
      },
      axiosConfig,
    );
  }

  function brandHasPermission(brand: Brand | null) {
    return identityStore.guard(BRAND.ADS.CAN_ACCESS_TIKTOK_ADS, brand);
  }

  function userHasPermission(brand: Brand | null) {
    return identityStore.guard(USER.ADS.CAN_ACCESS_TIKTOK_ADS, brand);
  }

  function userAndBrandHavePermission(brand: Brand | null) {
    return brandHasPermission(brand) && userHasPermission(brand);
  }

  return {
    adAccountsList,
    adAccountsLoading,
    adCampaigns,
    adCampaignsList,
    adCampaignsLoading,
    adAccountToCurrency,
    adAccountsError,
    adCampaignsError,
    getAdAccounts,
    getAdCampaigns,
    brandHasPermission,
    userHasPermission,
    userAndBrandHavePermission,
  };
}
